import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Modal from 'react-modal';
import './AuthScreen.css';
import Icon from '../../components/AppIcon';

// Configuration de react-modal
Modal.setAppElement('#root');

const API_BASE_URL = 'https://api.autocasses.com/api';
const OTP_API_URL = 'https://api.autocasses.com/api/otp/send';

// Configurer axios une seule fois
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Styles pour react-modal
const customModalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
  content: {
    position: 'relative',
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
    width: '100%',
    maxWidth: '400px',
    padding: '30px',
    borderRadius: '20px',
    border: 'none',
    background: 'white',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    animation: 'slideUp 0.3s ease',
    maxHeight: '90vh',
    overflow: 'visible',
  },
};

const AuthScreen = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState('phone');
  const [focusedInput, setFocusedInput] = useState(null);
  
  // États pour la modal OTP
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [otpData, setOtpData] = useState(null);
  const [registerData, setRegisterData] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [otpError, setOtpError] = useState('');
  const otpInputsRef = useRef([]);

  // Références pour les animations
  const contentRef = useRef(null);

  // Gestion du compte à rebours
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    // Animation d'entrée
    if (contentRef.current) {
      contentRef.current.style.opacity = '0';
      contentRef.current.style.transform = 'translateY(20px)';
      
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
          contentRef.current.style.opacity = '1';
          contentRef.current.style.transform = 'translateY(0)';
        }
      }, 100);
    }

    // Réinitialiser les champs
    setPhone('');
    setEmail('');
    setPassword('');
    setFullName('');
    setFocusedInput(null);
  }, [isLogin]);

  const storeAuthData = async (token, userData) => {
    try {
      localStorage.setItem('userToken', token);
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.setItem('preferredLoginMethod', loginMethod);
    } catch (error) {
      console.error('Erreur de stockage:', error);
      throw error;
    }
  };

  const validatePhone = (phoneNumber) => {
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    
    if (cleanPhone.length < 9) {
      return { isValid: false, message: 'Numéro trop court (9 chiffres minimum)' };
    }
    
    const firstDigit = cleanPhone.charAt(0);
    if (!['6', '2', '7', '8', '9'].includes(firstDigit)) {
      return { isValid: false, message: 'Numéro invalide. Doit commencer par 6,2,7,8 ou 9' };
    }
    
    return { isValid: true, cleanPhone };
  };

  const sendOtp = async (phoneNumber) => {
    try {
      const response = await axios.post(OTP_API_URL, {
        phoneNumber: `+237${phoneNumber}`
      });
      
      if (response.data.success) {
        setOtpData(response.data.data);
        setCountdown(600); // 10 minutes en secondes
        return { success: true, data: response.data.data };
      } else {
        return { success: false, message: response.data.message || 'Erreur lors de l\'envoi du code' };
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi OTP:', error);
      let errorMessage = 'Impossible d\'envoyer le code de vérification';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { success: false, message: errorMessage };
    }
  };

  const handleLogin = async () => {
    // Validation
    if (loginMethod === 'phone' && !phone) {
      alert('Champ requis', 'Veuillez entrer votre numéro de téléphone');
      return;
    }
    
    if (loginMethod === 'email' && !email) {
      alert('Champ requis', 'Veuillez entrer votre email');
      return;
    }
    
    if (!password) {
      alert('Champ requis', 'Veuillez entrer votre mot de passe');
      return;
    }

    setIsLoading(true);

    try {
      // Préparer les données pour l'API
      const loginData = {
        password: password
      };

      // Ajouter soit email soit telephone selon la méthode
      if (loginMethod === 'phone') {
        const validation = validatePhone(phone);
        if (!validation.isValid) {
          alert('Numéro invalide', validation.message);
          setIsLoading(false);
          return;
        }
        loginData.telephone = validation.cleanPhone;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
          alert('Email invalide', 'Veuillez entrer un email valide');
          setIsLoading(false);
          return;
        }
        loginData.email = email.trim();
      }

      // Appel API avec axios
      const response = await api.post('/user/sign', loginData);
      
      // Vérifier si la réponse contient une erreur API
      if (response.data.error) {
        const errorMessage = response.data.error.message || 'Identifiants incorrects';
        alert('Échec de la connexion', errorMessage);
        return;
      }
      
      if (response.data.success === 1 && response.data.token) {
        await storeAuthData(response.data.token, response.data.userData);
        
        navigate('/', { 
          state: { 
            email: email || `${phone}`,
            loginMethod: loginMethod
          }
        });
      } else {
        alert('Erreur', response.data.message || 'Réponse inattendue du serveur');
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
      
      let errorMessage = 'Impossible de se connecter au serveur';
      
      if (error.response) {
        if (error.response.data) {
          if (error.response.data.error?.message) {
            errorMessage = error.response.data.error.message;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else {
            errorMessage = `Erreur ${error.response.status}`;
          }
        } else {
          errorMessage = `Erreur ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Pas de réponse du serveur. Vérifiez votre connexion internet.';
      } else {
        errorMessage = error.message || 'Erreur inconnue';
      }
      
      alert('Échec de la connexion', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    // Validation
    if (!fullName || !phone || !email || !password) {
      alert('Champs requis', 'Veuillez remplir tous les champs');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      alert('Email invalide', 'Veuillez entrer un email valide');
      return;
    }

    const phoneValidation = validatePhone(phone);
    if (!phoneValidation.isValid) {
      alert('Numéro invalide', phoneValidation.message);
      return;
    }

    if (password.length < 6) {
      alert('Mot de passe trop court', 'Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setIsLoading(true);

    try {
      // Envoyer le code OTP
      const otpResult = await sendOtp(phoneValidation.cleanPhone);
      
      if (otpResult.success) {
        // Stocker les données d'inscription pour plus tard
        const registerData = {
          nom: fullName.trim(),
          email: email.trim(),
          password: password,
          telephone: parseInt(phoneValidation.cleanPhone, 10),
          isVerified: false,
          inActive: false,
          adresse: "Adresse non spécifiée"
        };
        
        setRegisterData(registerData);
        setIsOtpModalOpen(true);
        setOtpError('');
        setOtpCode(['', '', '', '', '', '']);
        
        // Focus sur le premier champ OTP après un court délai
        setTimeout(() => {
          if (otpInputsRef.current[0]) {
            otpInputsRef.current[0].focus();
          }
        }, 100);
      } else {
        alert('Erreur', otpResult.message || 'Impossible d\'envoyer le code de vérification');
      }
    } catch (error) {
      console.error('Erreur d\'envoi OTP:', error);
      alert('Erreur', 'Impossible d\'envoyer le code de vérification');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Ne permettre que des chiffres
    if (value && !/^\d$/.test(value)) return;
    
    const newOtpCode = [...otpCode];
    newOtpCode[index] = value;
    setOtpCode(newOtpCode);
    setOtpError('');
    
    // Passer au champ suivant
    if (value && index < 5 && otpInputsRef.current[index + 1]) {
      otpInputsRef.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Navigation avec les touches fléchées et suppression
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      e.preventDefault();
      otpInputsRef.current[index - 1].focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      otpInputsRef.current[index - 1].focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      otpInputsRef.current[index + 1].focus();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      verifyOtp();
    }
  };

  const verifyOtp = async () => {
    const enteredOtp = otpCode.join('');
    
    if (enteredOtp.length !== 6) {
      setOtpError('Veuillez entrer les 6 chiffres du code');
      return;
    }

    setIsOtpLoading(true);
    setOtpError('');

    // Vérifier si le code correspond
    if (otpData && enteredOtp === otpData.otpCode) {
      try {
        // Enregistrer l'utilisateur
        const response = await api.post('/user/register', registerData);
        
        if (response.data.success) {
          setIsOtpModalOpen(false);
          setOtpCode(['', '', '', '', '', '']);
          setOtpData(null);
          setRegisterData(null);
          setCountdown(0);
          
          alert(
            'Succès', 
            'Compte créé avec succès !',
            [
              {
                text: 'OK',
                onPress: () => {
                  setIsLogin(true);
                  setEmail(registerData.email);
                  setPhone(registerData.telephone.toString());
                  setPassword('');
                  setFullName('');
                }
              }
            ]
          );
        } else {
          setOtpError(response.data.error?.message || "Erreur lors de l'inscription");
        }
      } catch (error) {
        console.error('Erreur d\'inscription:', error);
        let errorMessage = "Impossible de créer le compte";
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        setOtpError(errorMessage);
      }
    } else {
      setOtpError('Code incorrect. Veuillez réessayer.');
    }
    
    setIsOtpLoading(false);
  };

  const resendOtp = async () => {
    if (countdown > 0 || !phone) return;
    
    setIsResendingOtp(true);
    setOtpError('');
    
    try {
      const phoneNumber = phone.replace(/\D/g, '');
      const result = await sendOtp(phoneNumber);
      
      if (result.success) {
        setOtpCode(['', '', '', '', '', '']);
        if (otpInputsRef.current[0]) {
          otpInputsRef.current[0].focus();
        }
      } else {
        setOtpError(result.message);
      }
    } catch (error) {
      console.error('Erreur lors du renvoi:', error);
      setOtpError('Erreur lors du renvoi du code');
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleAuth = () => {
    if (isLogin) {
      handleLogin();
    } else {
      handleRegister();
    }
  };

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
  };

  const formatPhoneNumber = (text) => {
    const cleaned = text.replace(/\D/g, '');
    if (cleaned.length > 9) {
      return phone;
    }
    return cleaned;
  };

  const handleForgotPassword = () => {
    alert('Information', 'Contactez le support pour réinitialiser votre mot de passe');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const closeOtpModal = () => {
    setIsOtpModalOpen(false);
    setOtpCode(['', '', '', '', '', '']);
    setOtpError('');
    setOtpData(null);
    setRegisterData(null);
    setCountdown(0);
  };

  return (
    <>
      <div className="auth-container">
        <div className="keyboard-view">
          <div className="scroll-content">
            <div ref={contentRef} className="auth-content">
              {/* Header */}
              <div className="auth-header">
                <div className="illustration-container">
                  <div className="icon-circle">
                    <Icon 
                      name={isLogin ? "Lock" : "UserPlus"} 
                      size={40} 
                      color="#3b82f6" 
                    />
                  </div>
                </div>
                
                <h1 className="auth-title">
                  {isLogin ? 'Content de vous revoir' : 'Créer un compte'}
                </h1>
                <p className="auth-subtitle">
                  {isLogin 
                    ? 'Connectez-vous pour continuer' 
                    : 'Rejoignez notre communauté'
                  }
                </p>
              </div>

              {/* Form Container */}
              <div className="form-container">
                {/* Toggle Switch */}
                <div className="toggle-container">
                  <button
                    className={`toggle-option ${isLogin ? 'active' : ''}`}
                    onClick={() => !isLogin && toggleAuthMode()}
                    disabled={isLoading}
                  >
                    <span className={`toggle-text ${isLogin ? 'active' : ''}`}>
                      Connexion
                    </span>
                  </button>
                  
                  <button
                    className={`toggle-option ${!isLogin ? 'active' : ''}`}
                    onClick={() => isLogin && toggleAuthMode()}
                    disabled={isLoading}
                  >
                    <span className={`toggle-text ${!isLogin ? 'active' : ''}`}>
                      Inscription
                    </span>
                  </button>
                  
                  <div 
                    className="toggle-indicator"
                    style={{
                      left: isLogin ? '8px' : 'calc(50% + 8px)',
                    }}
                  />
                </div>

                {/* Login Method Selector (uniquement pour connexion) */}
                {isLogin && (
                  <div className="method-selector">
                    <button
                      className={`method-button ${loginMethod === 'phone' ? 'active' : ''}`}
                      onClick={() => {
                        setLoginMethod('phone');
                        setEmail('');
                      }}
                      disabled={isLoading}
                    >
                      <Icon 
                        name="Phone" 
                        size={18} 
                        color={loginMethod === 'phone' ? '#3b82f6' : '#6B7280'} 
                      />
                      <span className={`method-text ${loginMethod === 'phone' ? 'active' : ''}`}>
                        Téléphone
                      </span>
                    </button>
                    
                    <button
                      className={`method-button ${loginMethod === 'email' ? 'active' : ''}`}
                      onClick={() => {
                        setLoginMethod('email');
                        setPhone('');
                      }}
                      disabled={isLoading}
                    >
                      <Icon 
                        name="Mail" 
                        size={18} 
                        color={loginMethod === 'email' ? '#3b82f6' : '#6B7280'} 
                      />
                      <span className={`method-text ${loginMethod === 'email' ? 'active' : ''}`}>
                        Email
                      </span>
                    </button>
                  </div>
                )}

                {/* Formulaire */}
                <div className="auth-form">
                  {/* Champ nom complet (inscription seulement) */}
                  {!isLogin && (
                    <div className="input-container">
                      <label className="input-label">Nom complet</label>
                      <div className="input-wrapper">
                        <Icon name="User" size={20} color="#9CA3AF" className="input-icon" />
                        <input
                          type="text"
                          className={`input ${focusedInput === 'fullName' ? 'focused' : ''}`}
                          placeholder="Votre nom complet"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          onFocus={() => setFocusedInput('fullName')}
                          onBlur={() => setFocusedInput(null)}
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  )}

                  {/* Champ téléphone */}
                  {(!isLogin || loginMethod === 'phone') && (
                    <div className="input-container">
                      <label className="input-label">
                        {isLogin ? 'Numéro de téléphone' : 'Téléphone'}
                      </label>
                      <div className="input-wrapper">
                        <div className="phone-prefix">
                          <span className="phone-prefix-text">+237</span>
                        </div>
                        <input
                          type="tel"
                          className={`input phone-input ${focusedInput === 'phone' ? 'focused' : ''}`}
                          placeholder="690000000"
                          value={phone}
                          onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                          maxLength={9}
                          onFocus={() => setFocusedInput('phone')}
                          onBlur={() => setFocusedInput(null)}
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  )}

                  {/* Champ email */}
                  {(!isLogin || loginMethod === 'email') && (
                    <div className="input-container">
                      <label className="input-label">
                        {isLogin ? 'Adresse email' : 'Email'}
                      </label>
                      <div className="input-wrapper">
                        <Icon name="Mail" size={20} color="#9CA3AF" className="input-icon" />
                        <input
                          type="email"
                          className={`input ${focusedInput === 'email' ? 'focused' : ''}`}
                          placeholder="votre@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onFocus={() => setFocusedInput('email')}
                          onBlur={() => setFocusedInput(null)}
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  )}

                  {/* Champ mot de passe */}
                  <div className="input-container">
                    <label className="input-label">Mot de passe</label>
                    <div className="input-wrapper">
                      <Icon name="Lock" size={20} color="#9CA3AF" className="input-icon" />
                      <input
                        type={showPassword ? "text" : "password"}
                        className={`input password-input ${focusedInput === 'password' ? 'focused' : ''}`}
                        placeholder="Votre mot de passe"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setFocusedInput('password')}
                        onBlur={() => setFocusedInput(null)}
                        disabled={isLoading}
                      />
                      <button
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isLoading}
                        type="button"
                      >
                        <Icon 
                          name={showPassword ? "EyeOff" : "Eye"} 
                          size={20} 
                          color="#6B7280" 
                        />
                      </button>
                    </div>
                  </div>

                  {/* Mot de passe oublié */}
                  {isLogin && (
                    <button 
                      className="forgot-password"
                      onClick={handleForgotPassword}
                      disabled={isLoading}
                      type="button"
                    >
                      <span className="forgot-password-text">
                        Mot de passe oublié ?
                      </span>
                    </button>
                  )}

                  {/* Bouton principal */}
                  <button
                    className={`auth-button ${isLoading ? 'disabled' : ''}`}
                    onClick={handleAuth}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="loader"></div>
                    ) : (
                      <span className="auth-button-text">
                        {isLogin ? 'Se connecter' : 'Créer mon compte'}
                      </span>
                    )}
                  </button>
                </div>

                {/* Basculer de méthode */}
                {isLogin && (
                  <button
                    className="switch-method-button"
                    onClick={() => {
                      if (loginMethod === 'phone') {
                        setLoginMethod('email');
                        setPhone('');
                      } else {
                        setLoginMethod('phone');
                        setEmail('');
                      }
                    }}
                    disabled={isLoading}
                    type="button"
                  >
                    <span className="switch-method-text">
                      Se connecter avec {loginMethod === 'phone' ? 'email' : 'téléphone'}
                    </span>
                  </button>
                )}

                {/* Conditions */}
                {!isLogin && (
                  <p className="terms-text">
                    En vous inscrivant, vous acceptez nos conditions d'utilisation
                  </p>
                )}
              </div>

              {/* Basculer entre connexion/inscription */}
              <div className="switch-mode">
                <span className="switch-mode-text">
                  {isLogin ? 'Pas encore de compte ? ' : 'Déjà un compte ? '}
                </span>
                <button 
                  onClick={toggleAuthMode} 
                  disabled={isLoading}
                  className="switch-mode-link"
                  type="button"
                >
                  <span className="switch-mode-link-text">
                    {isLogin ? "S'inscrire" : 'Se connecter'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal OTP avec react-modal */}
      <Modal
        isOpen={isOtpModalOpen}
        onRequestClose={closeOtpModal}
        style={customModalStyles}
        contentLabel="Vérification OTP"
        shouldCloseOnOverlayClick={!isOtpLoading}
        shouldCloseOnEsc={!isOtpLoading}
      >
        <div className="otp-modal-content">
          <div className="otp-modal-header">
            <Icon name="Shield" size={32} color="#3b82f6" />
            <h2 className="otp-modal-title">Vérification du numéro</h2>
            <p className="otp-modal-subtitle">
              Entrez le code à 6 chiffres envoyé au <br />
              <strong>+237{phone}</strong>
            </p>
          </div>

          <div className="otp-input-container">
            {otpCode.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (otpInputsRef.current[index] = el)}
                type="text"
                className="otp-input"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                maxLength={1}
                disabled={isOtpLoading}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            ))}
          </div>

          {otpError && (
            <div className="otp-error-message">
              <Icon name="AlertCircle" size={16} color="#ef4444" />
              <span>{otpError}</span>
            </div>
          )}

          <div className="otp-timer">
            {countdown > 0 ? (
              <span className="otp-timer-text">
                Code valide pendant : {formatTime(countdown)}
              </span>
            ) : (
              <span className="otp-timer-expired">
                Code expiré
              </span>
            )}
          </div>

          <div className="otp-actions">
            <button
              className={`otp-resend-button ${countdown > 0 ? 'disabled' : ''}`}
              onClick={resendOtp}
              disabled={isResendingOtp || countdown > 0 || isOtpLoading}
            >
              {isResendingOtp ? (
                <div className="small-loader"></div>
              ) : (
                <span>Renvoyer le code</span>
              )}
            </button>

            <button
              className="otp-verify-button"
              onClick={verifyOtp}
              disabled={isOtpLoading}
            >
              {isOtpLoading ? (
                <div className="small-loader"></div>
              ) : (
                <span>Vérifier</span>
              )}
            </button>
          </div>

          <button
            className="otp-close-button"
            onClick={closeOtpModal}
            disabled={isOtpLoading}
            aria-label="Fermer"
          >
            <Icon name="X" size={20} color="#64748b" />
          </button>
        </div>
      </Modal>
    </>
  );
};

export default AuthScreen;